import type { QueryResourcesResult } from '@ns/plugin-arm-proxy-client';
import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { QueryClient, useQuery } from '@tanstack/react-query';

import {
  queryResourcesOptions,
  type QueryResourcesQueryArgs,
  type QueryResourcesQueryKey,
} from './query-resources-options';
import { useArmTransport } from './transport';

/**
 * The query client every `useQueryResources` call shares unless one is passed.
 *
 * @remarks
 * Our own client, passed per call. That keeps this plugin free of a
 * `QueryClientProvider` in the host app. The body of `useQueryClient` is,
 * verbatim: `const client = React.useContext(QueryClientContext)`, then
 * `if (queryClient) return queryClient`, and only then
 * `if (!client) throw new Error('No QueryClient set, ...')`. So the context IS
 * read first, but a passed-in client returns before the throw, and `useContext`
 * on an empty context does not throw. That is why it works without a provider.
 * Source: TanStack/query, `packages/react-query/src/QueryClientProvider.tsx`,
 * branch `main`, accessed 2026-09-11.
 *
 * Retry belongs in `@ns/http-request`, because that layer knows the status codes
 * and `Retry-After`. TanStack therefore turns it off, so the two do not stack
 * once a caller passes `resilience.retry`. Without that option NEITHER layer
 * repeats anything, and that is intended.
 *
 * An answer stays fresh for two minutes. The picker validators read through
 * this same cache at submit, so a change at the source inside that window is
 * not seen until it passes. Azure data changes rarely, which is why the window
 * is this long.
 */
export const armQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      gcTime: 5 * 60_000,
      refetchOnWindowFocus: false,
      retry: false,
      staleTime: 120_000,
    },
  },
});

/**
 * The arguments of `useQueryResources`: those of `queryResources`, minus what
 * the hook fills in itself.
 */
export type UseQueryResourcesArgs = QueryResourcesQueryArgs;

/**
 * Runs `queryResources` as a TanStack query, with the transport and discovery
 * taken from the Backstage APIs.
 *
 * @remarks
 * The shared client is the DEFAULT, not the only option. A test passes its own,
 * for example with `gcTime: 0`, so two tests cannot leak into each other through
 * a cache that outlives them in the process.
 *
 * @typeParam TSelected - What `options.select` turns the result into.
 * @param args - The query, its scopes, Microsoft's `options` and `resilience`.
 * @param options - Every TanStack option except `queryFn` and `queryKey`.
 * @param queryClient - The client to use, `armQueryClient` by default.
 * @returns The TanStack query result.
 */
export function useQueryResources<TSelected = QueryResourcesResult>(
  args: UseQueryResourcesArgs,
  options?: Omit<
    UseQueryOptions<
      QueryResourcesResult,
      Error,
      TSelected,
      QueryResourcesQueryKey
    >,
    'queryFn' | 'queryKey'
  >,
  queryClient: QueryClient = armQueryClient,
): UseQueryResult<TSelected, Error> {
  const recipe = queryResourcesOptions(args, useArmTransport());

  return useQuery(
    {
      ...options,
      ...recipe,
    },
    queryClient,
  );
}
